import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Truck, Wrench, Fuel } from "lucide-react";
import { fechaMX } from "@/lib/format";

const VACIO = { placas: "", marca: "", modelo: "", anio: "", capacidad_kg: "", estatus: "Activo", notas: "" };
const EST_CLS = { Activo: "bg-emerald-100 text-emerald-700", "En taller": "bg-amber-100 text-amber-700", Baja: "bg-gray-200 text-gray-600" };

function CamionDialog({ cur, setCur, onSaved }) {
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setCur({ ...cur, [k]: e.target.value });
  const guardar = async () => {
    if (!cur.placas.trim()) return toast.error("Las placas son obligatorias");
    setSaving(true);
    const body = { ...cur, anio: cur.anio ? Number(cur.anio) : null, capacidad_kg: cur.capacidad_kg ? Number(cur.capacidad_kg) : null };
    try {
      if (cur.id) await api.put(`/camiones/${cur.id}`, body); else await api.post("/camiones", body);
      toast.success("Camión guardado"); setCur(null); onSaved();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!cur} onOpenChange={(o) => !o && setCur(null)}>
      <DialogContent className="max-w-lg" data-testid="camion-dialog">
        <DialogHeader><DialogTitle>{cur?.id ? "Editar camión" : "Nuevo camión"}</DialogTitle></DialogHeader>
        {cur && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Placas *</Label><Input value={cur.placas} onChange={set("placas")} data-testid="camion-placas" /></div>
            <div><Label>Marca</Label><Input value={cur.marca || ""} onChange={set("marca")} data-testid="camion-marca" /></div>
            <div><Label>Modelo</Label><Input value={cur.modelo || ""} onChange={set("modelo")} data-testid="camion-modelo" /></div>
            <div><Label>Año</Label><Input type="number" value={cur.anio || ""} onChange={set("anio")} data-testid="camion-anio" /></div>
            <div><Label>Capacidad (kg)</Label><Input type="number" value={cur.capacidad_kg || ""} onChange={set("capacidad_kg")} data-testid="camion-capacidad" /></div>
            <div className="col-span-2"><Label>Estatus</Label>
              <Select value={cur.estatus} onValueChange={(v) => setCur({ ...cur, estatus: v })}>
                <SelectTrigger data-testid="camion-estatus"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(EST_CLS).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notas</Label><Textarea rows={2} value={cur.notas || ""} onChange={set("notas")} data-testid="camion-notas" /></div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => setCur(null)}>Cancelar</Button><Button onClick={guardar} disabled={saving} data-testid="camion-guardar">{saving ? "Guardando..." : "Guardar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistorialCamion({ camiones }) {
  const [cam, setCam] = useState("");
  const [viajes, setViajes] = useState([]);
  useEffect(() => { if (!cam && camiones.length) setCam(camiones[0].id); }, [camiones, cam]);
  useEffect(() => { if (cam) api.get("/viajes", { params: { camion_id: cam } }).then(({ data }) => setViajes(data || [])); }, [cam]);
  return (
    <div className="space-y-3" data-testid="historial-camion">
      {camiones.length === 0 && <p className="text-sm text-muted-foreground" data-testid="historial-camion-vacio">Da de alta un camión para ver su historial de viajes.</p>}
      <Select value={cam} onValueChange={setCam}>
        <SelectTrigger className="w-72" data-testid="historial-camion-select"><SelectValue placeholder="Elige un camión" /></SelectTrigger>
        <SelectContent>{camiones.map((c) => <SelectItem key={c.id} value={c.id}>{c.placas} {c.marca || ""} {c.modelo || ""}</SelectItem>)}</SelectContent>
      </Select>
      {cam && (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Viaje</TableHead><TableHead>Fecha</TableHead><TableHead>Chofer</TableHead><TableHead>Entregas</TableHead><TableHead>Kg</TableHead><TableHead>Estatus</TableHead></TableRow></TableHeader>
            <TableBody>
              {viajes.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Sin viajes registrados</TableCell></TableRow>}
              {viajes.map((v) => (
                <TableRow key={v.id}><TableCell>{v.folio}</TableCell><TableCell>{fechaMX(v.fecha)}</TableCell><TableCell>{v.chofer?.full_name}</TableCell>
                  <TableCell>{v.hechas}/{v.total}</TableCell><TableCell>{v.kg.toLocaleString("es-MX")}</TableCell><TableCell>{v.estatus}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

const Proximamente = ({ icon: Icon, titulo, testid }) => (
  <div className="rounded-lg border border-dashed p-10 text-center text-muted-foreground" data-testid={testid}>
    <Icon className="h-8 w-8 mx-auto mb-2" /><p className="font-medium">{titulo}</p><p className="text-sm">Próximamente</p>
  </div>
);

export default function Flotilla() {
  const [camiones, setCamiones] = useState([]);
  const [cur, setCur] = useState(null);
  const cargar = () => api.get("/camiones").then(({ data }) => setCamiones(data || []));
  useEffect(() => { cargar(); }, []);
  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el camión ${c.placas}?`)) return;
    try { await api.delete(`/camiones/${c.id}`); toast.success("Camión eliminado"); cargar(); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  };
  return (
    <div className="space-y-6" data-testid="flotilla-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold flex items-center gap-2"><Truck className="h-6 w-6" />Flotilla</h1><p className="text-sm text-muted-foreground">Camiones, historial de viajes y mantenimientos</p></div>
        <Button onClick={() => setCur({ ...VACIO })} data-testid="camion-nuevo"><Plus className="h-4 w-4 mr-1" />Nuevo camión</Button>
      </div>
      <Tabs defaultValue="camiones">
        <TabsList><TabsTrigger value="camiones" data-testid="tab-camiones">Camiones</TabsTrigger><TabsTrigger value="viajes" data-testid="tab-historial-camion">Historial de viajes</TabsTrigger><TabsTrigger value="mant" data-testid="tab-mantenimientos">Mantenimientos</TabsTrigger><TabsTrigger value="comb" data-testid="tab-combustible">Combustible</TabsTrigger></TabsList>
        <TabsContent value="camiones">
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Placas</TableHead><TableHead>Marca / modelo</TableHead><TableHead>Año</TableHead><TableHead>Capacidad</TableHead><TableHead>Viajes</TableHead><TableHead>Estatus</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
              <TableBody>
                {camiones.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground" data-testid="camiones-vacio">Aún no hay camiones. Da de alta el primero.</TableCell></TableRow>}
                {camiones.map((c) => (
                  <TableRow key={c.id} data-testid={`camion-fila-${c.id}`}>
                    <TableCell className="font-semibold">{c.placas}</TableCell><TableCell>{[c.marca, c.modelo].filter(Boolean).join(" ") || "-"}</TableCell>
                    <TableCell>{c.anio || "-"}</TableCell><TableCell>{c.capacidad_kg ? `${Number(c.capacidad_kg).toLocaleString("es-MX")} kg` : "-"}</TableCell>
                    <TableCell>{c.viajes}</TableCell><TableCell><Badge className={`${EST_CLS[c.estatus]} border-transparent`}>{c.estatus}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="outline" size="icon" onClick={() => setCur({ ...VACIO, ...c })} data-testid={`camion-editar-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="outline" size="icon" className="ml-2 text-red-600" onClick={() => eliminar(c)} data-testid={`camion-eliminar-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="viajes"><HistorialCamion camiones={camiones} /></TabsContent>
        <TabsContent value="mant"><Proximamente icon={Wrench} titulo="Mantenimientos" testid="mantenimientos-proximamente" /></TabsContent>
        <TabsContent value="comb"><Proximamente icon={Fuel} titulo="Combustible" testid="combustible-proximamente" /></TabsContent>
      </Tabs>
      <CamionDialog cur={cur} setCur={setCur} onSaved={cargar} />
    </div>
  );
}
